import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Instagram, Award, Download, X, ChevronLeft, ChevronRight, CalendarCheck } from "lucide-react";
import { api, API } from "../lib/api";
import BookingDialog from "../components/BookingDialog";
import { usePageMeta } from "../lib/usePageMeta";
import { fmtHeight, fmtCmIn, fmtShoe } from "../lib/measure";

export default function ModelProfile() {
  const { slug } = useParams();
  const [model, setModel] = useState(null);
  const [works, setWorks] = useState([]);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState({ open: false, photos: [], index: 0 });

  useEffect(() => {
    api.get(`/models/${slug}`).then((r) => setModel(r.data)).catch(() => setError("Talent not found"));
  }, [slug]);

  useEffect(() => {
    api.get("/latest").then((r) => setWorks(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  usePageMeta(
    model ? `${model.name} — Ege Models` : undefined,
    model ? `${model.name} · Women board · Ege Models, model management, Rotterdam.` : undefined,
    model?.cover_image
  );

  const openLightbox = (photos, index) => setLightbox({ open: true, photos, index });
  const closeLightbox = () => setLightbox((l) => ({ ...l, open: false }));

  if (error) {
    return (
      <div className="pt-40 max-w-[1600px] mx-auto px-6 lg:px-12">
        <p className="label-overline text-zinc-500">404</p>
        <h1 className="font-serif text-6xl mt-4">{error}</h1>
        <Link to="/models" className="btn-ghost mt-8">Back to the board</Link>
      </div>
    );
  }

  if (!model) {
    return <div className="pt-40 max-w-[1600px] mx-auto px-6 lg:px-12 label-overline text-zinc-500">Loading…</div>;
  }

  const m = model.measurements || {};
  const polaroids = model.polaroids || [];
  const tests = model.gallery || [];
  const reel = model.video_reel;
  const placements = model.placements || [];
  const heroImage = model.cover_image || polaroids[0] || tests[0];
  const divisionLabel = "Women";
  const boardLink = "/models";
  const firstName = model.name.split(" ")[0];
  const restName = model.name.split(" ").slice(1).join(" ");
  const archiveWorks = works.filter((w) => w.model_slug === model.slug || w.model_id === model.id);

  return (
    <div data-testid="model-profile-page" className="pb-16">
      {/* FULL-BLEED HERO — dark canvas, contained image (never cropped), name overlaid */}
      <section className="relative h-[92svh] min-h-[560px] bg-[#0A0A0A] overflow-hidden" data-testid="model-hero">
        {heroImage && (
          <>
            <img
              src={heroImage}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover scale-110 blur-3xl opacity-40"
            />
            <img
              src={heroImage}
              alt={model.name}
              className="relative w-full h-full object-contain"
            />
          </>
        )}
        <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none"/>

        <Link
          to={boardLink}
          data-testid="model-back-link"
          className="absolute top-24 left-6 lg:left-12 label-overline text-white/70 hover:text-white transition-colors inline-flex items-center gap-2"
        >
          <ArrowLeft size={13}/> {divisionLabel}
        </Link>

        <div className="absolute bottom-0 left-0 right-0 px-6 lg:px-12 pb-10">
          <div className="max-w-[1600px] mx-auto">
            <p className="label-overline text-white/60" data-testid="model-board">Ege Models · {divisionLabel}</p>
            <h1
              data-testid="model-name"
              className="font-serif text-white tracking-tighter font-light mt-3 leading-[0.85] text-5xl sm:text-7xl lg:text-[8.5rem]"
            >
              {firstName}{" "}
              <span className="italic text-white/50">{restName}</span>
            </h1>
          </div>
        </div>
      </section>

      {/* STICKY SPEC BAR — measurements + actions always in reach */}
      <div className="sticky top-20 z-30 bg-[#FFFFFF]/95 backdrop-blur-xl border-b border-black/10" data-testid="model-measurements">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 flex items-center gap-7 lg:gap-10 overflow-x-auto no-scrollbar py-3.5">
          <SpecInline label="Height" value={fmtHeight(m.height_cm)} />
          <SpecInline label="Bust" value={fmtCmIn(m.bust_cm)} />
          <SpecInline label="Waist" value={fmtCmIn(m.waist_cm)} />
          <SpecInline label="Hips" value={fmtCmIn(m.hips_cm)} />
          <SpecInline label="Shoe" value={fmtShoe(m.shoe_eu)} />
          <SpecInline label="Hair" value={m.hair || "—"} />
          <SpecInline label="Eyes" value={m.eyes || "—"} />

          <div className="ml-auto flex items-center gap-2.5 shrink-0 pl-4">
            {model.instagram && (
              <a
                href={`https://instagram.com/${model.instagram.replace(/^@/, "")}`}
                target="_blank" rel="noreferrer"
                data-testid="model-instagram"
                aria-label="Instagram"
                className="p-2 text-zinc-600 hover:text-zinc-500 transition-colors"
              >
                <Instagram size={16}/>
              </a>
            )}
            <a
              href={`${API}/models/${slug}/sedcard.pdf`}
              target="_blank"
              rel="noreferrer"
              data-testid="profile-sedcard-download"
              className="inline-flex items-center gap-2 label-overline text-zinc-700 hover:text-zinc-500 border border-black/15 hover:border-black px-3.5 py-2.5 transition-colors"
            >
              <Download size={12}/> Sedcard
            </a>
            <BookingDialog model={model}>
              <button data-testid="profile-book-header" className="btn-primary inline-flex items-center gap-2 !py-2.5 !px-4">
                <CalendarCheck size={13}/> Book
              </button>
            </BookingDialog>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        {/* BIO — editorial pull-quote */}
        {model.bio && (
          <p className="mt-16 max-w-3xl font-serif text-2xl md:text-3xl tracking-tight font-light leading-snug text-zinc-800" data-testid="model-bio">
            {model.bio}
          </p>
        )}

        {/* PORTFOLIO — editorial masonry, first thing bookers see */}
        {tests.length > 0 && (
          <section className="mt-20" data-testid="portfolio-section">
            <div className="flex items-baseline justify-between flex-wrap gap-2 border-b border-black/10 pb-4 mb-8">
              <div>
                <p className="label-overline text-zinc-500">Portfolio</p>
                <h2 className="font-serif text-4xl lg:text-5xl tracking-tighter font-light mt-1">In print.</h2>
              </div>
              <div className="flex items-center gap-4">
                <p className="label-overline text-zinc-500">
                  {tests.length} {tests.length === 1 ? "shot" : "shots"}
                </p>
                <button
                  type="button"
                  data-testid="portfolio-open-slideshow"
                  onClick={() => openLightbox(tests, 0)}
                  className="label-overline text-zinc-700 hover:text-zinc-500 border border-black/20 hover:border-black px-3 py-1.5 transition-colors"
                >
                  Open slideshow →
                </button>
              </div>
            </div>

            <div className="columns-2 md:columns-3 lg:columns-4 gap-3 md:gap-4" style={{ columnFill: "balance" }}>
              {tests.map((src, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`portfolio-${i}`}
                  onClick={() => openLightbox(tests, i)}
                  className="block w-full mb-3 md:mb-4 break-inside-avoid relative overflow-hidden bg-[#F5F5F5] group focus:outline-none focus:ring-1 focus:ring-black"
                >
                  <img
                    src={src}
                    alt=""
                    loading="lazy"
                    className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.02]"
                  />
                  <span className="absolute top-3 left-3 font-mono text-[10px] text-white bg-black/50 backdrop-blur-sm px-1.5 py-0.5">
                    {String(i + 1).padStart(2, "0")} / {String(tests.length).padStart(2, "0")}
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* POLAROIDS — raw digitals */}
        {polaroids.length > 0 && (
          <section className="mt-28" data-testid="polaroids-section">
            <div className="border-b border-black/10 pb-4 mb-8">
              <p className="label-overline text-zinc-500">Digitals</p>
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tighter font-light mt-1">Polaroids.</h2>
              <p className="text-zinc-500 mt-3 text-sm max-w-2xl">
                Natural light, no makeup, no retouch — the raw image partner agencies look at first.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {polaroids.map((src, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`polaroid-${i}`}
                  onClick={() => openLightbox(polaroids, i)}
                  className="relative aspect-[3/4] overflow-hidden bg-[#EFEFEC] group focus:outline-none focus:ring-1 focus:ring-black"
                >
                  <img
                    src={src}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-25"
                  />
                  <img
                    src={src}
                    alt=""
                    loading="lazy"
                    className="relative w-full h-full object-contain transition-transform duration-700 group-hover:scale-[1.02]"
                  />
                  <span className="absolute top-2 left-2 font-mono text-[10px] text-white bg-black/60 px-1.5 py-0.5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* MOTION / SIGNATURE — full-bleed cinematic moment */}
        {(reel || tests[1]) && (
          <section className="mt-28 -mx-6 lg:-mx-12">
            <div className="relative w-full aspect-[4/5] sm:aspect-[3/2] bg-black overflow-hidden">
              {reel ? (
                <video
                  src={reel}
                  autoPlay muted loop playsInline preload="metadata"
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover scale-110 blur-3xl opacity-40"
                />
              ) : (
                <img
                  src={tests[1]}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover scale-110 blur-3xl opacity-40"
                />
              )}
              {reel ? (
                <video
                  src={reel}
                  autoPlay muted loop playsInline preload="metadata"
                  className="relative w-full h-full object-contain"
                />
              ) : (
                <img
                  src={tests[1]}
                  alt=""
                  className="relative w-full h-full object-contain"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent pointer-events-none"/>
              <div className="absolute bottom-6 left-6 lg:bottom-12 lg:left-12 text-white">
                <p className="label-overline tracking-[0.25em] text-white/70">
                  {reel ? "Motion" : "In frame"}
                </p>
                <p className="font-serif italic text-2xl lg:text-4xl mt-2">{model.name}.</p>
              </div>
            </div>
          </section>
        )}

        {/* PLACEMENTS */}
        {placements.length > 0 && (
          <section className="mt-28" data-testid="model-placements">
            <p className="label-overline text-zinc-500 inline-flex items-center gap-2"><Award size={12}/> International placements</p>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tighter font-light mt-2">Placed with.</h2>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-black/10 border border-black/10">
              {placements.map((p, i) => (
                <div key={i} className="bg-white p-6">
                  <p className="font-serif text-2xl tracking-tight">{p.agency}</p>
                  {p.city && <p className="label-overline text-zinc-500 mt-1">{p.city}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* IN THE ARCHIVE — published work featuring this talent */}
        {archiveWorks.length > 0 && (
          <section className="mt-28" data-testid="model-archive">
            <p className="label-overline text-zinc-500">In the archive</p>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tighter font-light mt-2">Selected work.</h2>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {archiveWorks.slice(0, 4).map((w) => (
                <Link
                  key={w.id}
                  to={`/news/${w.id}`}
                  data-testid={`model-archive-${w.id}`}
                  className="group block focus:outline-none focus:ring-1 focus:ring-black"
                >
                  <div className="relative aspect-[3/4] bg-[#F5F5F5] overflow-hidden">
                    <img
                      src={w.image_url}
                      alt={w.brand}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                    />
                  </div>
                  <p className="mt-3 font-serif text-lg leading-tight">{w.brand}</p>
                  {w.subtitle && <p className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1">{w.subtitle}</p>}
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="mt-28 border-t border-black/10 pt-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <p className="label-overline text-zinc-500">Book / Place</p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tighter font-light mt-3 leading-[0.95]">
                Interested in booking<br/><span className="italic text-zinc-600">{firstName}?</span>
              </h2>
              <p className="text-zinc-700 mt-6 leading-relaxed max-w-xl text-sm">
                For direct bookings, editorial features and campaigns, send a brief through our booking
                form. For partner-agency placement inquiries, write to{" "}
                <a href="mailto:info@egemodels.com" className="link-underline">info@egemodels.com</a>.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-3">
              <BookingDialog model={model}>
                <button data-testid="profile-book-cta" className="btn-primary inline-flex items-center gap-2">
                  <CalendarCheck size={15}/> Book {firstName}
                </button>
              </BookingDialog>
              <a href="mailto:info@egemodels.com" data-testid="profile-email-cta" className="btn-ghost">Email management</a>
            </div>
          </div>
        </section>
      </div>

      {/* FULLSCREEN LIGHTBOX */}
      <ProfileLightbox state={lightbox} onClose={closeLightbox} setState={setLightbox}/>
    </div>
  );
}

function ProfileLightbox({ state, onClose, setState }) {
  const { open, photos, index } = state;
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft")  setState((s) => ({ ...s, index: (s.index - 1 + s.photos.length) % s.photos.length }));
      if (e.key === "ArrowRight") setState((s) => ({ ...s, index: (s.index + 1) % s.photos.length }));
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onClose, setState]);

  if (!open) return null;
  const prev = () => setState((s) => ({ ...s, index: (s.index - 1 + s.photos.length) % s.photos.length }));
  const next = () => setState((s) => ({ ...s, index: (s.index + 1) % s.photos.length }));

  // Portal into document.body so we escape any ancestor `transform`
  // (e.g. the .page-fade wrapper) that would break position:fixed.
  return createPortal(
    <div
      data-testid="profile-lightbox"
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="profile-lightbox-close"
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
        aria-label="Close"
      >
        <X size={26}/>
      </button>
      {photos.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 lg:left-8 text-white/70 hover:text-white p-2 bg-black/50 rounded-full"
            aria-label="Previous"
          >
            <ChevronLeft size={28}/>
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 lg:right-8 text-white/70 hover:text-white p-2 bg-black/50 rounded-full"
            aria-label="Next"
          >
            <ChevronRight size={28}/>
          </button>
        </>
      )}
      <div onClick={(e) => e.stopPropagation()} className="max-w-[92vw] max-h-[88vh] flex flex-col items-center gap-3">
        <img
          src={photos[index]}
          alt=""
          className="max-w-full max-h-[82vh] object-contain shadow-2xl"
        />
        <p className="text-white/60 text-xs uppercase tracking-wider font-mono">
          {index + 1} / {photos.length}
        </p>
      </div>
    </div>,
    document.body
  );
}

function SpecInline({ label, value }) {
  const isDual = value && typeof value === "object";
  return (
    <div className="shrink-0">
      <p className="label-overline text-zinc-400 !text-[9px]">{label}</p>
      <p className="font-serif text-base md:text-lg leading-tight whitespace-nowrap mt-0.5">
        {isDual ? (
          <>
            {value.primary}{" "}
            <span className="font-mono text-[10px] tracking-wider text-zinc-400">{value.secondary}</span>
          </>
        ) : (
          value
        )}
      </p>
    </div>
  );
}

/* Unit conversion helpers live in lib/measure.js (shared with the board hover card). */
